import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useAuth0, User } from '@auth0/auth0-react';
import axios, { AxiosError, AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import strings, { url as urlConst } from 'constants/index';
import { TABLE_PRESETS } from 'constants/table-presets';

import { normalizeJwt } from './../utils/normalizers.utils';

export const roles = {
  ['fixture-api:user-management']: strings.role_user_management,
  ['fixture-api:admin']: strings.role_curator,
  ['fixture-api:general']: strings.role_viewer,
};

const permissions = {
  permissions: [],
  restrictions: [],
  isAdmin: true,
  id: null,
  createdOn: '0001-01-01T00:00:00',
  modifiedOn: null,
  groups: ['4928464b-d483-4596-aad9-72eb9c50baa8'],
  updatesCount: 0,
};

interface CurationUser extends User {
  permissions?: string[];
  restrictions?: string[];
  isAdmin?: boolean;
  id?: string;
  createdOn?: string;
  modifiedOn?: string;
  updatesCount?: number;
  role?: string;
  tablePresets?: {
    [key: string]: Array<TablePreset>;
  };
}

interface AuthContextType {
  user?: CurationUser;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [scope, setScope] = useState(null);
  const { user, loginWithRedirect, logout, isAuthenticated, getAccessTokenSilently, isLoading } =
    useAuth0<User>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const handleAccessToken = useCallback(async () => {
    const accessToken = await getAccessTokenSilently();
    const normalizedToken = normalizeJwt(accessToken);
    setScope(normalizedToken.scope);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated) {
      handleAccessToken();
    }
  }, [handleAccessToken, isAuthenticated]);

  useEffect(() => {
    axios.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const resultConfig = config;
        resultConfig.withCredentials = true;

        const accessToken = await getAccessTokenSilently();
        const headers = AxiosHeaders.from(resultConfig.headers);
        headers.set('Authorization', `Bearer ${accessToken}`);
        resultConfig.headers = headers;

        return resultConfig;
      },
      (error: AxiosError) => Promise.reject(error),
    );

    axios.interceptors.response.use(
      async (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          if (!isLoading && !isAuthenticated) {
            const url = `${pathname}?${searchParams.toString()}`;
            localStorage.setItem('url', url);
            loginWithRedirect();
          }
        }

        return Promise.reject(error);
      },
    );
  }, [getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, pathname, searchParams]);

  useEffect(() => {
    if (!isLoading) {
      if (searchParams.get('code')) {
        if (isAuthenticated) {
          axios.post(urlConst.user_access, null, { disableSuccessAlert: true });
          const url = localStorage.getItem('url');
          if (url !== '/') {
            navigate(url, { replace: true });
          }
          localStorage.removeItem('url');
        }
      } else {
        if (!isAuthenticated) {
          const url = `${pathname}?${searchParams.toString()}`;
          localStorage.setItem('url', url);
          loginWithRedirect();
        }
      }
    }
  }, [navigate, isAuthenticated, loginWithRedirect, isLoading, searchParams, pathname]);

  const getRole = (scope: string) => {
    if (!scope) {
      return strings.role_guest;
    }

    for (const [key, roleName] of Object.entries(roles)) {
      if (scope.includes(key)) {
        return roleName;
      }
    }

    return strings.role_guest;
  };

  const memoiziedValue = useMemo(
    () => ({
      user: { ...user, ...permissions, tablePresets: TABLE_PRESETS, scope, role: getRole(scope) },
      logout: () => logout({ returnTo: `${window.location.protocol}//${window.location.host}` }),
    }),
    [user, scope, logout],
  );

  return (
    <AuthContext.Provider value={memoiziedValue}>{user && scope && children}</AuthContext.Provider>
  );
};

export default function useAuth() {
  return useContext(AuthContext);
}
